/* eslint-disable import/named */
import { createContext, useCallback, useContext, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import client from '@flow/libs/oneflow-client';
import { importContract, fetchAgreement } from '@flow/libs/oneflow-client/agreements';

import type { Asset } from '@app/reducers/current-contract';
import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import agreementsReducer from '@app/reducers/entities/agreements';
import agreementTemplatesReducer, {
  getTemplatesWithAgreementsSelector,
} from '@app/reducers/entities/agreement-templates';

import {
  getWorkspaceQuerySelector,
  isAiImport,
} from '@app/components/modals/import-contract/utils';
import { AI, STANDARD } from '@app/components/modals/import-contract/constants';
import { getPdfBoxContent } from '@flow/libs/agreement/import';
import { checkAcl } from '@flow/libs/utils/acl';
import { useDocumentPdfListener } from '@app/components/modals/import-contract/hooks/use-document-pdf-listener';

import type { Box } from '@flow/libs/data-validators/entity-schemas/document-box';

type ExtractionState = {
  loading: boolean;
  error: null | Error;
};

type ImportMethod = 'standard' | 'ai';

type ImportContractContextType = {
  agreement: React.MutableRefObject<Oneflow.Agreement | undefined>;
  assetUploadErrorCode: string | number | undefined;
  extractionState: ExtractionState;
  importMethod: ImportMethod;
  isAiImportOptionEnabled: boolean;
  onAssetPagesDone: (responseJSON: Asset) => void;
  onFileUploadError: (errorCode: string | number) => void;
  pdfIsReady: boolean;
  prepareDocument: () => Promise<void>;
  removeContract: () => void;
  removeFile: () => void;
  resetExtractionState: () => void;
  resetFileUploadState: () => void;
  selectedUniqueKey?: number;
  setExtractionState: (state: ExtractionState) => void;
  setImportMethod: (method: ImportMethod) => void;
  setUploadedPdfName: (name: string | null) => void;
  setUploadInitiated: (state: boolean) => void;
  templatesLength: number | undefined;
  uploadDocument: ({ file, type }: { file: File; type: string }) => Promise<void>;
  uploadedPdfName: string | null;
  uploadInitiated: boolean;
  defaultExtractMethod: ImportMethod;
};

const ImportContractContext = createContext<ImportContractContextType | undefined>(undefined);

export const ImportContractProvider = ({
  selectedUniqueKey,
  children,
}: {
  selectedUniqueKey?: number;
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector);
  const isAiImportOptionEnabled = checkAcl(currentWorkspace.acl, 'collection:ai_import:use');
  const defaultExtractMethod = isAiImportOptionEnabled ? AI : STANDARD;
  const [importMethod, setImportMethod] = useState<ImportMethod>(defaultExtractMethod);
  const [uploadedPdfName, setUploadedPdfName] = useState<string | null>(null);
  const [extractionState, setExtractionState] = useState<ExtractionState>({
    loading: false,
    error: null,
  });
  const [uploadInitiated, setUploadInitiated] = useState<boolean>(false);
  const agreement = useRef<Oneflow.Agreement | undefined>(undefined);
  const [assetUploadErrorCode, setAssetUploadErrorCode] = useState<string | number | undefined>(
    undefined,
  );
  const queryName = useSelector((state) =>
    getWorkspaceQuerySelector(state, {
      queryName: 'active-templates-with-import-tag',
    }),
  );
  const query = useSelector((state) =>
    agreementTemplatesReducer.getQuerySelector(state, {
      name: queryName,
    }),
  );
  const templates = useSelector((state) =>
    getTemplatesWithAgreementsSelector(state, {
      ids: query.result,
    }),
  );
  const { pdfUploadStatus, resetPdfUploadStatus, subscribeToWebsocket } = useDocumentPdfListener();

  const resetExtractionState = useCallback(() => {
    setExtractionState({
      loading: false,
      error: null,
    });
  }, []);

  const removeContract = useCallback(() => {
    if (!agreement.current?.id) {
      return;
    }
    dispatch(
      agreementsReducer.removeAgreement({
        id: agreement.current?.id,
        pipe: {
          onSuccess: () => {
            agreement.current = undefined;
          },
        },
      }),
    );
  }, [
    dispatch,
  ]);

  const removeFile = useCallback(() => {
    resetPdfUploadStatus();
    setUploadedPdfName(null);
    setUploadInitiated(false);
    resetExtractionState();
    removeContract();
  }, [
    resetPdfUploadStatus,
    resetExtractionState,
    removeContract,
  ]);

  const prepareDocument = useCallback(() => {
    resetPdfUploadStatus();
    setUploadInitiated(true);

    const fetchContract = ({ result }: { result: number }) =>
      fetchAgreement({
        id: result,
      });

    const setContract = ({
      normalized,
    }: {
      normalized: {
        result: number;
        entities: {
          agreements: {
            [key: number]: Oneflow.Agreement;
          };
        };
      };
    }) => {
      const { result: id, entities } = normalized;
      const contract = entities.agreements[id];
      agreement.current = contract;
    };

    return importContract({
      workspaceId: currentWorkspace.id,
      folderId: selectedUniqueKey === -1 ? null : selectedUniqueKey,
    })
      .then(fetchContract)
      .then(setContract);
  }, [
    resetPdfUploadStatus,
    currentWorkspace.id,
    selectedUniqueKey,
  ]);

  const setAttachmentDocument = useCallback(
    ({
      agreementId,
      data,
      boxes,
    }: {
      agreementId: number | undefined;
      data: any[];
      boxes: Box[];
    }) => {
      if (!agreementId) {
        return;
      }
      dispatch(
        agreementsReducer.updateAttachmentBox({
          id: agreementId,
          data: {
            data,
            boxes,
          },
        }),
      );
    },
    [
      dispatch,
    ],
  );

  const onAssetPagesDone = useCallback(
    (responseJSON: Asset) => {
      setUploadedPdfName(responseJSON.assetName);

      const { boxes } = agreement.current || {};
      const boxesContent = getPdfBoxContent(responseJSON, boxes);

      setAttachmentDocument({
        agreementId: agreement.current?.id,
        data: [
          boxesContent.contentData,
        ],
        boxes: boxesContent.boxes,
      });
    },
    [
      setAttachmentDocument,
    ],
  );

  const uploadDocument = useCallback(
    ({ file, type }: { file: File; type: string }) => {
      const contractId = agreement.current?.id;

      subscribeToWebsocket(contractId);

      return client.uploadDocument({
        file,
        type,
        contractId,
      });
    },
    [
      subscribeToWebsocket,
    ],
  );

  const onFileUploadError = useCallback((errorCode: string | number) => {
    setAssetUploadErrorCode(errorCode);
  }, []);

  const resetFileUploadState = useCallback(() => {
    setAssetUploadErrorCode(undefined);
    setUploadInitiated(false);
  }, []);

  const contextValue = {
    agreement,
    assetUploadErrorCode,
    defaultExtractMethod,
    extractionState,
    importMethod,
    isAiImportOptionEnabled,
    onAssetPagesDone,
    onFileUploadError,
    pdfIsReady: Boolean(uploadedPdfName) && isAiImport(importMethod) && pdfUploadStatus === 'ready',
    prepareDocument,
    removeContract,
    removeFile,
    resetExtractionState,
    resetFileUploadState,
    selectedUniqueKey,
    setExtractionState,
    setImportMethod,
    setUploadedPdfName,
    setUploadInitiated,
    templatesLength: templates?.length,
    uploadDocument,
    uploadedPdfName,
    uploadInitiated,
  };

  return (
    <ImportContractContext.Provider value={contextValue}>{children}</ImportContractContext.Provider>
  );
};

export const useImportContractContext = () => {
  const context = useContext(ImportContractContext);
  if (!context) {
    throw new Error('useImportContractContext must be used within an ImportContractProvider');
  }
  return context;
};
